import React from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { DIMENSION_COLORS, SurveyRecord } from '../types';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface Props {
  records: SurveyRecord[];
}

const WHEEL_LABELS = ['身体健康', '心理健康', '关系健康', '事业发展', '财富自由', '价值意义'];

const dataLabelsPlugin = {
  id: 'customLabels',
  afterDraw(chart: any) {
    const { ctx, data, scales } = chart;
    const rScale = scales.r;
    if (!rScale) return;
    const centerX = rScale.xCenter;
    const centerY = rScale.yCenter;
    const radius = rScale.drawingArea + 14;

    data.labels.forEach((label: string, i: number) => {
      const angle = (Math.PI / 180) * ((360 / data.labels.length) * i - 90 - 360 / data.labels.length / 2);
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);

      ctx.save();
      ctx.font = 'bold 14px "PingFang SC", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#374151';
      ctx.fillText(label, x, y);
      ctx.restore();
    });
  },
};

const HappinessWheel: React.FC<Props> = ({ records }) => {
  if (records.length === 0) return null;

  const datasets = records.map((record, idx) => {
    const dimCodes = ['physical', 'mental', 'relationship', 'career', 'wealth', 'meaning'];
    const data = dimCodes.map(code => record.dimensionScores[code] || 0);
    const hue = (idx * 60) % 360;
    const color = idx === 0
      ? '#3498DB'
      : `hsl(${hue}, 70%, 50%)`;
    return {
      label: record.surveyDate,
      data,
      backgroundColor: idx === 0
        ? 'rgba(52, 152, 219, 0.15)'
        : `hsla(${hue}, 70%, 50%, 0.15)`,
      borderColor: color,
      pointBackgroundColor: color,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    };
  });

  const data = {
    labels: WHEEL_LABELS,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      r: {
        min: 0,
        max: 15,
        ticks: {
          stepSize: 3,
          font: { size: 11 },
          backdropColor: 'transparent',
        },
        grid: {
          color: 'rgba(0,0,0,0.08)',
        },
        angleLines: {
          color: 'rgba(0,0,0,0.12)',
        },
        pointLabels: {
          display: false, // We use custom labels
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          font: { size: 13 },
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.raw}分`,
        },
      },
    },
  };

  return (
    <Radar data={data} options={options} plugins={[dataLabelsPlugin]} />
  );
};

export default HappinessWheel;
